import { find, map, sortBy } from 'lodash';
import { produce } from 'immer';
import React, { FC, useEffect } from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';

import { Body, Section } from 'src/presentation/components/forms/Layout';
import businessHoursResource from 'src/actions/businessHours/businessHoursResource';
import { Button } from 'src/presentation/components/agent_desktop_components';
import Dropdown from 'src/presentation/components/forms/Dropdown';
import Footer from 'src/presentation/components/forms/Footer';
import Form, { FormEditorProps, useForm } from '../../forms/Form';
import { getAdminURL } from '../shared/utils/Utils';
import useResource from 'src/presentation/hooks/useResource';

const SLABusinessHoursForm: FC<FormEditorProps> = ({ errors }) => {
  const dispatch = useDispatch();

  const noPrimaryBusinessHours = 'NO_PRIMARY_BUSINESS_HOURS';
  const { values: businessHours } = useResource(businessHoursResource, { fetch: false }); // resource has been fetched already

  const primaryBusinessHours = find(businessHours, 'primary');
  const primaryBusinessHoursId = primaryBusinessHours ? primaryBusinessHours.id : noPrimaryBusinessHours;
  const item = { primaryBusinessHoursId };
  const originalPrimaryBusinessHoursId = item.primaryBusinessHoursId;

  const formContext = useForm({
    item,
    errors,
    resetErrors: businessHoursResource.actions.resetErrors,
    root: 'sla-business-hours',
    update: businessHoursResource.effects.replace,
  });

  const { workingState } = formContext;

  useEffect(() => {
    formContext.updateState({ ...workingState, primaryBusinessHoursId });
    // eslint-disable-next-line
  }, [primaryBusinessHoursId]);

  if (!businessHours || !businessHours.length) {
    return (
      <PaddedDescription data-aid="create-business-hours-description">
        You have no Business Hours created yet. To set up your schedule, go to{' '}
        <a data-aid="business-hours-page-link" href={getAdminURL('business-hours')}>
          Business Hours
        </a>
        .
      </PaddedDescription>
    );
  }

  const businessHoursOptions = sortBy(
    map(businessHours, businessHour => ({
      label: businessHour.name,
      value: businessHour.id,
    })),
    item => item.label.toLowerCase()
  );
  businessHoursOptions.unshift({ label: 'None', value: noPrimaryBusinessHours });

  const updatePrimaryBusinessHours = () => {
    let updatedBusinessHours;
    if (workingState.primaryBusinessHoursId === noPrimaryBusinessHours) {
      const currentPrimaryBusinessHours = find(businessHours, { id: originalPrimaryBusinessHoursId });
      updatedBusinessHours = produce(currentPrimaryBusinessHours, draft => {
        draft.primary = false;
      });
    } else {
      const currentPrimaryBusinessHours = find(businessHours, { id: workingState.primaryBusinessHoursId });
      updatedBusinessHours = produce(currentPrimaryBusinessHours, draft => {
        draft.primary = true;
      });
    }
    const toastMessage =
      'The Primary Business Hours has been updated. The changes will be reflected in reporting within 30 minutes.';
    const toastErrorMessage = 'Unable to update the Primary Business Hours. Please try again.';
    dispatch(
      businessHoursResource.effects.update({ id: updatedBusinessHours.id }, updatedBusinessHours, {
        customToastMsg: toastMessage,
        customErrorToastMsg: toastErrorMessage,
      })
    );
  };

  return (
    <Form context={formContext}>
      <Body data-aid="primary-businessHours-editor">
        <Section>
          <Dropdown
            attr="primaryBusinessHoursId"
            autoFocus
            data-aid="primary-businessHours-dropdown"
            label="Primary Business Hours"
            options={businessHoursOptions}
            placeholder="Select Primary Business Hours"
            value={workingState.primaryBusinessHoursId}
          />
        </Section>
      </Body>

      <Footer>
        <Button
          data-aid="primary-businessHours-editor-save"
          onClick={updatePrimaryBusinessHours}
          disabled={workingState.primaryBusinessHoursId === originalPrimaryBusinessHoursId}
        >
          Save
        </Button>
      </Footer>
    </Form>
  );
};

export default SLABusinessHoursForm;

const PaddedDescription = styled.div`
  padding-left: ${p => p.theme.spacing.insetLarge};
  color: ${p => p.theme.colors.black};
`;
